import React, { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { SelectValue } from 'antd/es/select';

import { LARGE_PAGE_SIZE } from '../../../../../constants';
import { alertsSelectorAdd, currentFolderIdSelector } from '../../../../../recoil';
import { defaultApi } from '../../../../../apis';
import IbTypography from '../../../../../components/common/IbTypography';
import IbInput from '../../../../../components/common/IbInput';
import ConnectorBlock from '../../../../../components/ConnectorBlock';
import {
  ELMA365ToolkitToolsetSettings,
  ELMA365ToolsetModel,
  ToolsetModel,
  ELMA365ToolsetStatus,
} from '../../../../../../gamma-api';
import IbSelect from '../../../../../components/common/IbSelect';
import IbTooltip from '../../../../../components/common/IbTooltip';
import { getTranslateFunc } from '../../../../../utils/stringUtil';

import {
  ELMA365_TOOL_NAME_EXAMPLE,
  FORM_ITEM_CLASS_NAME,
  PARAMETERS_CLASS_NAME,
  TOOL_NAMES_PREFIX_HINT_CLASS_NAME,
} from './const';

export interface IElma365ToolkitParametersProps {
  editToolset: ToolsetModel;
  onChange: (toolset: ToolsetModel) => void;
  onValidationStateChange?: (state: boolean) => void;
}

const Elma365ToolkitParameters: React.FC<IElma365ToolkitParametersProps> = ({
  editToolset,
  onChange,
  onValidationStateChange,
}) => {
  const translate = getTranslateFunc('Agent: Toolset: Modal: Settings:');

  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const folderId = useRecoilValue(currentFolderIdSelector);

  const [toolkitsSelectLoading, setToolkitsSelectLoading] = useState(true);
  const [toolkits, setToolkits] = useState<ELMA365ToolsetModel[]>([]);

  const elma365ToolsetSettings = editToolset.settings as ELMA365ToolkitToolsetSettings;

  const isValid = (settings: ELMA365ToolkitToolsetSettings) => !!settings.toolkit_id && !!settings.connector_id;

  const loadToolkits = async () => {
    setToolkitsSelectLoading(true);

    try {
      const response = await defaultApi.searchELMA365ToolkitToolsets(
        0,
        LARGE_PAGE_SIZE,
        false,
        undefined,
        [folderId],
        undefined,
        undefined,
        [ELMA365ToolsetStatus.Ready]
      );
      setToolkits(response.data.items || []);
    } catch (err) {
      addAlert({
        content: translate('Toolkits: Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    setToolkitsSelectLoading(false);
  };

  const onInitLoad = () => {
    loadToolkits().finally();
    onValidationStateChange?.(isValid(elma365ToolsetSettings));
  };
  useEffect(onInitLoad, []);

  const onToolkitChange = (value: SelectValue) => {
    const connectorId = toolkits.find((toolkit) => toolkit.toolset_id === value)?.connector_id ?? null;
    const newSettings = {
      ...elma365ToolsetSettings,
      toolkit_id: value as string,
      connector_id: connectorId,
    };
    onChange({ ...editToolset, settings: newSettings } as ToolsetModel);
    onValidationStateChange?.(isValid(newSettings));
  };

  const onConnectorChange = (value: SelectValue) => {
    const newSettings = { ...elma365ToolsetSettings, connector_id: value as string };
    onChange({ ...editToolset, settings: newSettings } as ToolsetModel);
    onValidationStateChange?.(isValid(newSettings));
  };

  const onToolNamesPrefixChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        tool_names_prefix: value,
      },
    } as ToolsetModel);
  };

  const toolkitOptions = toolkits.map((toolkit) => ({
    value: toolkit.toolset_id,
    label: toolkit.name,
  }));

  return (
    <div className={PARAMETERS_CLASS_NAME}>
      <div className={FORM_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {translate('Toolkit: Title')}
        </IbTypography.Paragraph>
        <IbSelect
          loading={toolkitsSelectLoading}
          options={toolkitOptions}
          value={elma365ToolsetSettings.toolkit_id}
          onChange={onToolkitChange}
        />
      </div>
      <div className={FORM_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {translate('Connector: Title')}
        </IbTypography.Paragraph>
        <ConnectorBlock connectorId={elma365ToolsetSettings.connector_id || ''} onChange={onConnectorChange} />
      </div>
      <div className={FORM_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {translate('Tool names prefix: Title')}
        </IbTypography.Paragraph>
        <IbInput value={elma365ToolsetSettings.tool_names_prefix} onChange={onToolNamesPrefixChange} />
        {elma365ToolsetSettings.tool_names_prefix && (
          <div className={TOOL_NAMES_PREFIX_HINT_CLASS_NAME}>
            <IbTypography.Paragraph disabled strong type="secondary">
              {translate('Tool names prefix: Example')}
            </IbTypography.Paragraph>
            <IbTooltip title={`${elma365ToolsetSettings.tool_names_prefix}${ELMA365_TOOL_NAME_EXAMPLE}`}>
              <IbTypography.Paragraph ellipsis type="secondary">
                {`${elma365ToolsetSettings.tool_names_prefix}${ELMA365_TOOL_NAME_EXAMPLE}`}
              </IbTypography.Paragraph>
            </IbTooltip>
          </div>
        )}
      </div>
    </div>
  );
};

export default Elma365ToolkitParameters;
